import ApiService from '@/services/ApiService';
import getDrfErrorMessage from '@/components/BranchesAndTeams/drfErrorMessage';

const PAGE_LIMIT = 100;

async function withDrfError(request) {
  try {
    return await request();
  } catch (error) {
    const message = getDrfErrorMessage(error.response);
    if (!message || message === error.message) {
      throw error;
    }
    const next = new Error(message);
    next.status = error.status;
    next.response = error.response;
    throw next;
  }
}

async function fetchAllPages(path, params = {}) {
  const results = [];
  let offset = 0;
  let hasNext = true;

  while (hasNext) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.append(key, value);
      }
    });
    searchParams.append('limit', String(PAGE_LIMIT));
    searchParams.append('offset', String(offset));

    // eslint-disable-next-line no-await-in-loop
    const response = await ApiService.get(`${path}?${searchParams.toString()}`);
    results.push(...(response.results || []));
    hasNext = Boolean(response.next);
    offset += PAGE_LIMIT;
  }

  return { results };
}

// Shared by every create/update endpoint that accepts a logo. Arrays (e.g.
// permission_group_ids, member_user_ids) need one `append` per item — FormData
// has no native array support, unlike JSON. An empty array must still append
// the key as `''`: omitting it looks like "field not sent" (keep existing
// M2M on update); `''` / JSON `[]` means clear the list.
function appendMultipartPayload(formData, payload) {
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      if (value.length === 0) {
        formData.append(key, '');
        return;
      }
      value.forEach((item) => formData.append(key, item));
      return;
    }
    formData.append(key, value);
  });
}

class BranchesAndTeamsService {
  static async listBranches({ search } = {}) {
    return fetchAllPages('/api3/company/branches/', { search });
  }

  static async listTeams({ search, includeDeleted } = {}) {
    return fetchAllPages('/api3/company/teams/', {
      search,
      include_deleted: includeDeleted ? 'true' : '',
    });
  }

  static async createTeam({
    name,
    logoFile,
    permissionGroupIds = [],
    memberUserIds = [],
  }) {
    const url = '/api3/company/teams/';
    const payload = {
      name,
      permission_group_ids: permissionGroupIds,
      member_user_ids: memberUserIds,
    };

    if (!logoFile) {
      return withDrfError(() => ApiService.post(url, payload));
    }

    const formData = new FormData();
    appendMultipartPayload(formData, payload);
    formData.append('logo', logoFile);

    return withDrfError(() => ApiService.post(url, formData, undefined, true));
  }

  static async createBranch({ logoFile, ...payload }) {
    const url = '/api3/company/branches/';

    if (!logoFile) {
      return withDrfError(() => ApiService.post(url, payload));
    }

    const formData = new FormData();
    appendMultipartPayload(formData, payload);
    formData.append('logo', logoFile);

    return withDrfError(() => ApiService.post(url, formData, undefined, true));
  }

  static async getBranch(branchId) {
    return ApiService.get(`/api3/company/branches/${branchId}/`);
  }

  static async updateBranch(branchId, { logoFile, ...payload }) {
    const url = `/api3/company/branches/${branchId}/`;

    if (!logoFile) {
      return withDrfError(() => ApiService.put(url, payload));
    }

    const formData = new FormData();
    appendMultipartPayload(formData, payload);
    formData.append('logo', logoFile);

    return withDrfError(() => ApiService.put(url, formData, undefined, true));
  }

  static async deleteBranch(branchId, { reassignTo } = {}) {
    const searchParams = new URLSearchParams();
    if (reassignTo) {
      searchParams.append('reassign_to', reassignTo);
    }
    const query = searchParams.toString();
    const url = `/api3/company/branches/${branchId}/${
      query ? `?${query}` : ''
    }`;

    return withDrfError(() => ApiService.delete(url));
  }

  static async deleteTeam(teamId) {
    return withDrfError(() =>
      ApiService.delete(`/api3/company/teams/${teamId}/`)
    );
  }

  static async getTeam(teamId) {
    return ApiService.get(`/api3/company/teams/${teamId}/`);
  }

  static async updateTeam(teamId, { logoFile, ...payload }) {
    const url = `/api3/company/teams/${teamId}/`;

    if (!logoFile) {
      return withDrfError(() => ApiService.put(url, payload));
    }

    const formData = new FormData();
    appendMultipartPayload(formData, payload);
    formData.append('logo', logoFile);

    return withDrfError(() => ApiService.put(url, formData, undefined, true));
  }

  static async listPermissionGroups() {
    return ApiService.get('/api3/company/permission-groups/');
  }

  static async searchAgents({ q } = {}) {
    const searchParams = new URLSearchParams();
    if (q) {
      searchParams.append('q', q);
    }
    return ApiService.get(`/api3/agent/?${searchParams.toString()}`);
  }

  static async listBranchMembers(branchId) {
    return fetchAllPages(`/api3/company/branches/${branchId}/members/`, {});
  }

  // Moves a company user into `branchId` and optionally drags their
  // listings along (panel "Change branch", TBR-6280). `withDrfError`
  // surfaces the backend's translated `user_id: [...]` / `detail` message
  // instead of a generic "Request failed".
  static async moveBranchMember(
    branchId,
    {
      userId,
      moveProducerProperties = false,
      moveLeadReceiverProperties = false,
    }
  ) {
    const url = `/api3/company/branches/${branchId}/members/`;
    return withDrfError(() =>
      ApiService.post(url, {
        user_id: userId,
        move_producer_properties: moveProducerProperties,
        move_lead_receiver_properties: moveLeadReceiverProperties,
      })
    );
  }

  static async listTeamMembers(teamId) {
    return fetchAllPages(`/api3/company/teams/${teamId}/members/`, {});
  }

  // Unlike branches, adding a team member is a plain N:M insert (no
  // property reassignment to decide), so there's no equivalent to
  // `moveProducerProperties`/`moveLeadReceiverProperties` here.
  static async addTeamMember(teamId, { userId }) {
    const url = `/api3/company/teams/${teamId}/members/`;
    return withDrfError(() => ApiService.post(url, { user_id: userId }));
  }

  static async removeTeamMember(teamId, userId) {
    const url = `/api3/company/teams/${teamId}/members/${userId}/`;
    return withDrfError(() => ApiService.delete(url));
  }
}

export default BranchesAndTeamsService;
